import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

/* ─────────────────────────────────────────────────────────
 * ANIMATION STORYBOARD — Intro Loader (Two-Phase)
 *
 * Read top-to-bottom. Each value is ms after trigger.
 *
 * PHASE 1 — Converge (halves meet in center, large)
 *    0ms   Overlay visible. Scroll locked.
 *          Left "ANGELA" at left edge, 5× scale, blur 8px, opacity 0
 *          Right "CAGLIA" at right edge, 5× scale, blur 8px, opacity 0
 *  100ms   Both halves fade in (opacity 0 → 1, blur 8 → 4px)
 *  200ms   Halves converge toward center, blur → 0
 *          Wrapper stays centered at 5× scale
 * 1400ms   Halves meet in center. Still 5× scale. Hold.
 *
 * PHASE 2 — Rise (combined logo moves to header)
 * 1800ms   Logo begins moving up to header, scale 5 → 1
 *          Easing: cubic-bezier(0.23, 1, 0.32, 1) — strong ease-out
 * 3200ms   Logo settled at header position, 1× scale
 *
 * PHASE 3 — Fade out
 * 3400ms   Overlay backdrop fades out
 * 4000ms   Complete. Scroll unlocked. Element removed from DOM.
 * ───────────────────────────────────────────────────────── */

const TIMING = {
	/* Phase 1: Converge */
	appear:        100,    // halves fade in
	convergeStart: 200,    // halves start moving to center
	convergeDone:  1400,   // halves meet in center (still large)
	hold:          1800,   // brief pause before rising

	/* Phase 2: Rise to header */
	riseDuration:  1400,   // ms for the rise + scale-down animation

	/* Phase 3: Fade */
	fadeDelay:     200,    // pause after rise before fading
	fadeDuration:  600,    // overlay fade out
}

const LOGO = {
	initialScale:  5,      // desktop starting scale
	mobileScale:   3,      // mobile starting scale (5× overflows on 375px)
	blurMax:       8,      // px — max blur at start
	blurMid:       4,      // px — blur when first visible
	midpointRatio: 0.3,    // how far halves are at converge-start (0 = center, 1 = edge)
	gapPx:         8,      // px — word space between halves (must match $logo-split-gap in SCSS)
	easing: 'cubic-bezier(0.23, 1, 0.32, 1)',  // strong ease-out (Emil)
}

/* SVG viewBox widths — must match the inline SVGs in intro-loader.liquid */
const SVG = {
	leftWidth:  447,       // viewBox width of left.svg ("ANGELA")
	rightWidth: 423,       // viewBox width of right.svg ("CAGLIA")
	totalWidth: 870,       // leftWidth + rightWidth
}

const OVERLAY = {
	easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
}

const STORAGE_KEY = 'ac_intro_seen'
const URL_PARAM = 'loader'

export class IntroLoader extends UcoastEl {
	static htmlSelector = 'intro-loader'

	private backdrop: HTMLElement
	private logoWrapper: HTMLElement
	private leftHalf: HTMLElement
	private rightHalf: HTMLElement
	private reducedMotion: boolean

	constructor() {
		super()
		this.backdrop = q.rs('.intro-loader__backdrop', this)
		this.logoWrapper = q.rs('.intro-loader__logo-wrapper', this)
		this.leftHalf = q.rs('.intro-loader__half--left', this)
		this.rightHalf = q.rs('.intro-loader__half--right', this)
		this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

		if (!this.shouldShow()) {
			this.remove()
			return
		}

		this.lockScroll()
		this.run()
	}

	private shouldShow(): boolean {
		const params = new URLSearchParams(window.location.search)
		if (params.has(URL_PARAM)) return true
		if (localStorage.getItem(STORAGE_KEY)) return false
		return true
	}

	private lockScroll() {
		document.body.style.overflow = 'hidden'
	}

	private unlockScroll() {
		document.body.style.overflow = ''
	}

	private async run() {
		try {
			localStorage.setItem(STORAGE_KEY, '1')
		} catch {
			// Private browsing — continue anyway
		}

		if (this.reducedMotion) {
			await this.runReduced()
		} else {
			await this.runFull()
		}

		this.unlockScroll()
		this.remove()
	}

	private async runReduced(): Promise<void> {
		this.leftHalf.style.transform = 'none'
		this.leftHalf.style.opacity = '1'
		this.leftHalf.style.filter = 'none'
		this.rightHalf.style.transform = 'none'
		this.rightHalf.style.opacity = '1'
		this.rightHalf.style.filter = 'none'
		this.logoWrapper.style.transform = 'translate(-50%, -50%)'

		await this.delay(800)
		await this.fadeOverlay()
	}

	private async runFull(): Promise<void> {
		const isMobile = window.innerWidth < 750
		const initialScale = isMobile ? LOGO.mobileScale : LOGO.initialScale

		// ─── Find the actual rendered logo element for precise alignment ───
		const logoSelector = isMobile
			? '.header__heading-logo-wrapper--mobile'
			: '.header__heading-logo-wrapper--desktop'
		const headerLogoEl = document.querySelector(logoSelector)
			?? document.querySelector('.header__heading-logo-wrapper')
			?? document.querySelector('.header__heading-link')

		let deltaX = 0
		let deltaY = 0

		if (headerLogoEl) {
			const logoRect = headerLogoEl.getBoundingClientRect()
			const targetX = logoRect.left + logoRect.width / 2
			const targetY = logoRect.top + logoRect.height / 2
			const vpCenterX = window.innerWidth / 2
			const vpCenterY = window.innerHeight / 2
			deltaX = targetX - vpCenterX
			deltaY = targetY - vpCenterY
		}

		// ─── Size the halves (account for gap between them) ───
		const logoWidth = isMobile
			? parseInt(this.dataset.mobileLogoWidth || '80', 10)
			: parseInt(this.dataset.logoWidth || '280', 10)

		const availableWidth = logoWidth - LOGO.gapPx
		const leftWidthPx = (SVG.leftWidth / SVG.totalWidth) * availableWidth
		const rightWidthPx = (SVG.rightWidth / SVG.totalWidth) * availableWidth

		this.leftHalf.style.width = `${leftWidthPx}px`
		this.rightHalf.style.width = `${rightWidthPx}px`

		const midBlur = LOGO.blurMid * 0.5
		const convergeDuration = TIMING.convergeDone

		// ─── Direction: horizontal on desktop, vertical on mobile ───
		const axis = isMobile ? 'Y' : 'X'
		const edgeOffset = isMobile ? window.innerHeight / 2 : window.innerWidth / 2
		const midEdge = edgeOffset * LOGO.midpointRatio

		// ═══════════════════════════════════════════
		// PHASE 1: Converge — halves meet in center
		// Wrapper stays at center, full scale
		// ═══════════════════════════════════════════

		this.logoWrapper.style.transform = `translate(-50%, -50%) scale(${initialScale})`

		// Left half: desktop = from left edge, mobile = from top
		const leftAnim = this.leftHalf.animate([
			{
				transform: `translate${axis}(-${edgeOffset}px)`,
				opacity: 0,
				filter: `blur(${LOGO.blurMax}px)`,
				offset: 0,
			},
			{
				transform: `translate${axis}(-${edgeOffset}px)`,
				opacity: 1,
				filter: `blur(${LOGO.blurMid}px)`,
				offset: TIMING.appear / convergeDuration,
			},
			{
				transform: `translate${axis}(-${midEdge}px)`,
				opacity: 1,
				filter: `blur(${midBlur}px)`,
				offset: TIMING.convergeStart / convergeDuration,
			},
			{
				transform: `translate${axis}(0)`,
				opacity: 1,
				filter: 'blur(0px)',
				offset: 1,
			},
		], {
			duration: convergeDuration,
			easing: LOGO.easing,
			fill: 'forwards',
		})

		// Right half: desktop = from right edge, mobile = from bottom
		this.rightHalf.animate([
			{
				transform: `translate${axis}(${edgeOffset}px)`,
				opacity: 0,
				filter: `blur(${LOGO.blurMax}px)`,
				offset: 0,
			},
			{
				transform: `translate${axis}(${edgeOffset}px)`,
				opacity: 1,
				filter: `blur(${LOGO.blurMid}px)`,
				offset: TIMING.appear / convergeDuration,
			},
			{
				transform: `translate${axis}(${midEdge}px)`,
				opacity: 1,
				filter: `blur(${midBlur}px)`,
				offset: TIMING.convergeStart / convergeDuration,
			},
			{
				transform: `translate${axis}(0)`,
				opacity: 1,
				filter: 'blur(0px)',
				offset: 1,
			},
		], {
			duration: convergeDuration,
			easing: LOGO.easing,
			fill: 'forwards',
		})

		// Wait for convergence to finish
		await leftAnim.finished

		// Brief hold — logo sits large and centered
		await this.delay(TIMING.hold - TIMING.convergeDone)

		// ═══════════════════════════════════════════
		// PHASE 2: Rise — logo moves up to header, scales down
		// ═══════════════════════════════════════════

		const riseAnim = this.logoWrapper.animate([
			{
				transform: `translate(-50%, -50%) scale(${initialScale})`,
			},
			{
				transform: `translate(calc(-50% + ${deltaX}px), calc(-50% + ${deltaY}px)) scale(1)`,
			},
		], {
			duration: TIMING.riseDuration,
			easing: LOGO.easing,
			fill: 'forwards',
		})

		await riseAnim.finished

		// ═══════════════════════════════════════════
		// PHASE 3: Fade out
		// ═══════════════════════════════════════════

		await this.delay(TIMING.fadeDelay)
		await this.fadeOverlay()
	}

	private fadeOverlay(): Promise<void> {
		return new Promise((resolve) => {
			const anim = this.animate([
				{ opacity: 1 },
				{ opacity: 0 },
			], {
				duration: TIMING.fadeDuration,
				easing: OVERLAY.easing,
				fill: 'forwards',
			})
			anim.onfinish = () => resolve()
		})
	}

	private delay(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms))
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.unlockScroll()
	}
}
